import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import { ArrowLeft, Tag } from '@phosphor-icons/react';
import {
	Drawer,
	DrawerContent,
	DrawerTitle,
} from '../../../../components/ui/drawer';
import { Spinner } from '@/components/ui/spinner';
import PrimaryButton from '../../../../components/atoms/Button';
import ProductCardSimple from '../../../../pages/plansAndPricesV2/organisms/ProductCardSimple';
import { useCatalog } from '../../../../hooks/useCatalog';
import useAddToCartV2 from '../../../../hooks/useAddToCartV2';
import { buildCheckoutPayload } from '../../../../shared/checkout';
import { setShowModalCheckout } from '../../../../redux/slices/plans.slice';
import { formatPrice } from '../../../../shared/utils/general.util';
import imgBannerAsat from '../../../../assets/img/png/plan-card-entrepreneur.webp';
import { useLinkingTracking } from '../orchestration/useLinkingTracking';
import type { LinkingMethod } from '../../useLinkingWorkflow';

/**
 * De dónde entró el usuario a la oferta. `credentials_error` = falló su
 * vinculación y tocó "¿Problemas con tus credenciales?"; `rfc_not_filed` =
 * declaró en el drawer de RFC que nunca tramitó su RFC.
 */
export type AsatPromoEntryPoint = 'credentials_error' | 'rfc_not_filed';

type AsatPromoDrawerProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	initialRequirement?: string;
	method: LinkingMethod;
	entryPoint: AsatPromoEntryPoint;
};

type View = 'promo' | 'requirement';

/**
 * Precio de ASAT dentro del flujo de vinculación, en centavos.
 *
 * Se fija desde el cliente con `custom_product.total_paid`, que el backend
 * honra como precio final del item PORQUE el producto ASAT trae
 * `custom_price = true` en el catálogo. Si ese flag se apagara, el backend
 * ignora el monto en silencio y cobra el precio de lista — sin error. Por eso
 * la oferta se pinta SIEMPRE desde esta constante y desde el catálogo: lo que
 * el usuario ve es exactamente lo que se manda en el payload.
 *
 * Vive aquí y no en el catálogo porque es la oferta de ESTA superficie: quien
 * llega a este drawer no pudo vincular o nunca tramitó su RFC, y el trámite es
 * justo lo que le falta para poder usar heru.
 */
const ASAT_LINKING_OFFER_CENTS = 9_900;

const AsatPromoDrawer = ({
	open,
	onOpenChange,
	initialRequirement,
	method,
	entryPoint,
}: AsatPromoDrawerProps) => {
	const dispatch = useDispatch();
	const { addToCart } = useAddToCartV2();
	const { trackAction } = useLinkingTracking();
	const { plans, isLoading } = useCatalog('asat');
	const [view, setView] = useState<View>('promo');
	const [requirement, setRequirement] = useState('');
	const [isActivating, setIsActivating] = useState(false);

	const plan = plans[0];
	const price = plan?.one_time;
	const listCents = price?.final ?? 0;
	const offerCents = price
		? Math.min(ASAT_LINKING_OFFER_CENTS, listCents)
		: ASAT_LINKING_OFFER_CENTS;
	const savingsCents = listCents - offerCents;
	const discountPct =
		listCents > 0 ? Math.round((savingsCents / listCents) * 100) : 0;

	// El catálogo puede no haber resuelto cuando se abre el drawer, así que el
	// bloque de precios solo viaja en las acciones (activar / agregar), que
	// ocurren con la tarjeta ya pintada.
	const offerPayload = {
		entry_point: entryPoint,
		offer_price_cents: offerCents,
		list_price_cents: listCents,
		discount_pct: discountPct,
	};

	useEffect(() => {
		if (open) {
			setView(initialRequirement ? 'requirement' : 'promo');
			setRequirement(initialRequirement ?? '');
			trackAction(method, 'asat_promo_viewed', {
				entry_point: entryPoint,
				initial_view: initialRequirement ? 'requirement' : 'promo',
			});
		}
	}, [open, initialRequirement, method, entryPoint, trackAction]);

	const handleAddToCart = async () => {
		if (!price?.product_version_id || !requirement.trim() || isActivating)
			return;
		setIsActivating(true);
		// Close the promo and surface the session-wide checkout (host resets to
		// 'global' on open, so GlobalCheckoutModal — mounted over onboarding in
		// SessionShell — renders the cart in place instead of navigating away.
		onOpenChange(false);
		dispatch(setShowModalCheckout(true));
		try {
			await addToCart({
				payload: [
					buildCheckoutPayload({
						source: 'sat_linking_asat_promo',
						productVersionId: price.product_version_id,
						additionalInfo: {
							asat_information: { message: requirement.trim() },
						},
						customProduct: { total_paid: offerCents },
					}),
				],
			});
			trackAction(method, 'asat_added_to_cart', {
				...offerPayload,
				product_version_id: price.product_version_id,
			});
		} catch {
			trackAction(method, 'asat_add_to_cart_failed', {
				...offerPayload,
				product_version_id: price.product_version_id,
			});
			toast.error('No pudimos agregar el servicio al carrito.');
		} finally {
			setIsActivating(false);
		}
	};

	const offerTag =
		savingsCents > 0 ? (
			<span className='bg-h-success-90 text-h-success-30 inline-flex w-fit items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold'>
				<Tag size={12} weight='fill' />
				Oferta · {discountPct}% de descuento
			</span>
		) : undefined;

	return (
		<Drawer open={open} onOpenChange={onOpenChange}>
			<DrawerContent className='inset-x-auto left-1/2 w-full max-w-lg -translate-x-1/2'>
				<DrawerTitle className='sr-only'>
					Trámites fiscales ante el SAT
				</DrawerTitle>

				{view === 'requirement' && (
					<div className='flex flex-col gap-1 px-5 pt-2 pb-3 pr-14'>
						<button
							type='button'
							onClick={() => setView('promo')}
							className='text-h-primary flex w-fit items-center gap-1 text-sm font-medium'>
							<ArrowLeft size={16} weight='bold' />
							Volver
						</button>
						<h2 className='text-h-text-heading text-xl font-bold'>
							¿Qué servicio necesitas?
						</h2>
					</div>
				)}

				<div className='max-h-[85dvh] overflow-y-auto px-4 pt-4 pb-6'>
					{view === 'promo' ? (
						<>
							<p className='text-h-text-medium mb-4 text-sm leading-relaxed'>
								¿Nunca has tramitado tu RFC? Nuestros especialistas lo tramitan
								por ti, y por venir desde aquí lo activas a precio especial.
							</p>
							{isLoading || !plan || !price ? (
								<div className='flex justify-center py-10'>
									<Spinner className='size-6' />
								</div>
							) : (
								<ProductCardSimple
									imgBanner={imgBannerAsat}
									name={plan.display_name}
									description={plan.tagline ?? ''}
									benefitsTitle='Trámites fiscales incluidos en tu plan'
									benefits={plan.benefits ?? []}
									ctaLabel='Activar servicio'
									ctaAction={() => {
										trackAction(method, 'asat_activate_clicked', offerPayload);
										setView('requirement');
									}}
									price={formatPrice(offerCents)}
									discount={
										savingsCents > 0 ? `$${formatPrice(listCents)}` : undefined
									}
									priceDescription={
										savingsCents > 0
											? `Ahorras $${formatPrice(savingsCents)} en tu trámite`
											: undefined
									}
									badge={offerTag}
									className='shadow-none'
								/>
							)}
						</>
					) : (
						<div className='flex flex-col gap-4 px-1'>
							{offerTag && (
								<div className='bg-h-neutral-98 flex items-center justify-between gap-2 rounded-xl px-3 py-2.5'>
									{offerTag}
									<p className='text-h-text-heading text-sm font-bold'>
										${formatPrice(offerCents)}{' '}
										<span className='text-h-text-light text-xs font-normal line-through'>
											${formatPrice(listCents)}
										</span>
									</p>
								</div>
							)}
							<p className='text-h-text-medium text-sm leading-relaxed'>
								Describe con tanto detalle como sea posible qué trámite o ayuda
								fiscal requieres. Así nuestro especialista contable podrá
								asistirte mejor.
							</p>
							<textarea
								autoFocus
								placeholder='Describe tu requerimiento'
								rows={4}
								className='border-h-neutral-90 focus:ring-ring w-full resize-none rounded-lg border p-2 text-base md:text-sm outline-none focus:ring-1'
								value={requirement}
								onChange={(e) => setRequirement(e.target.value)}
							/>
							<PrimaryButton
								label={`Agregar al carrito · $${formatPrice(offerCents)}`}
								onClick={handleAddToCart}
								loading={isActivating}
								disabled={!requirement.trim()}
							/>
						</div>
					)}
				</div>
			</DrawerContent>
		</Drawer>
	);
};

export default AsatPromoDrawer;
